import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AttentionTriangleIcon } from '@rsos/assets/icons';
import { Modal, Button } from '@rsos/base-components';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/base-components/Modal/ModalComponents';
import type { InteragencyDirectoryEntry } from '../../../reduxDataStore/interagency/interagencyAPI';
import {
  useGetInteragencyDirectoryQuery,
  useCreateInteragencyConversationMutation,
} from '../../../reduxDataStore/interagency/interagencyAPI';
import {
  setSelectedInteragencyChat,
  setPendingInteragencyMessage,
} from '../../../reduxDataStore/interagency/interagencySlice';
import styles from './NewAgencyConversationModal.module.scss';

interface NewAgencyConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgName: string | undefined;
}

const DIRECTORY_CACHE_MAX_AGE_SECONDS = 15;

/**
 * Modal opened from the "+" button in AgencyConversationsList.
 * Fetches the interagency directory so the user can select one or more
 * agencies, then creates a new interagency conversation via the
 * text_to_911 API. Closes on success; the parent list refreshes
 * automatically through RTK Query tag invalidation.
 */
function NewAgencyConversationModal({
  isOpen,
  onClose,
  orgName,
}: NewAgencyConversationModalProps) {
  const [selectedAgencies, setSelectedAgencies] = useState<
    InteragencyDirectoryEntry[]
  >([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [isCreateError, setIsCreateError] = useState(false);

  const {
    data: agencies = [],
    isLoading: isLoadingAgencies,
    isError: isDirectoryError,
  } = useGetInteragencyDirectoryQuery(
    { orgName: orgName ?? '' },
    {
      skip: !orgName || !isOpen,
      refetchOnMountOrArgChange: DIRECTORY_CACHE_MAX_AGE_SECONDS,
    },
  );

  const dispatch = useDispatch();

  const [createConversation, createStates] =
    useCreateInteragencyConversationMutation();

  const availableAgencies = agencies.filter(
    agency =>
      !selectedAgencies.some(s => s.account_id === agency.account_id) &&
      agency.display_name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleClose = () => {
    setSelectedAgencies([]);
    setSearchQuery('');
    setMessage('');
    setIsCreateError(false);
    onClose();
  };

  const handleSelectAgency = (agency: InteragencyDirectoryEntry) => {
    setSelectedAgencies(prev => [...prev, agency]);
    setSearchQuery('');
  };

  const handleRemoveAgency = (accountId: string) => {
    setSelectedAgencies(prev => prev.filter(a => a.account_id !== accountId));
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleStartConversation = () => {
    const canStartConversation =
      selectedAgencies.length > 0 && orgName && message.trim();
    if (!canStartConversation) return;

    setIsCreateError(false);
    createConversation({
      orgName,
      participants: selectedAgencies.map(a => ({
        account_id: a.account_id,
        authority_display_name: a.display_name,
      })),
    })
      .unwrap()
      .then(response => {
        dispatch(setPendingInteragencyMessage(message.trim()));
        dispatch(setSelectedInteragencyChat(response.conversation_id));
        handleClose();
      })
      .catch((err: unknown) => {
        setIsCreateError(true);
        // eslint-disable-next-line no-console -- Log creation failures for Sentry
        console.error('Failed to create interagency conversation:', err);
      });
  };

  return (
    <Modal
      isOpened={isOpen}
      onClose={handleClose}
      width="420px"
      padding="0"
      fitContent
      id="modal-new-agency-conversation"
    >
      <ModalHeader
        onClose={handleClose}
        padding="24px 24px 16px"
        borderBottom="none"
      >
        <div className={styles.modalTitle}>New Agency Conversation</div>
      </ModalHeader>
      <ModalBody
        px="24px"
        mb={0}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="lightLineDivider"
        py="16px"
      >
        <div className={styles.selectLabel}>Select Agencies</div>
        <div className={styles.inputContainer}>
          {selectedAgencies.map(agency => (
            <div key={agency.account_id} className={styles.chip}>
              <span>{agency.display_name}</span>
              <button
                className={styles.chipRemove}
                onClick={() => handleRemoveAgency(agency.account_id)}
                aria-label={`Remove ${agency.display_name}`}
                type="button"
              >
                ×
              </button>
            </div>
          ))}
          <input
            className={styles.searchInput}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={
              selectedAgencies.length === 0 ? 'Search agencies...' : ''
            }
          />
        </div>
        {isLoadingAgencies && (
          <div className={styles.directoryStatus}>Loading agencies...</div>
        )}
        {isDirectoryError && (
          <div className={styles.directoryError}>
            Failed to load agencies. Please try again.
          </div>
        )}
        {!isLoadingAgencies &&
          !isDirectoryError &&
          availableAgencies.length > 0 && (
            <div className={styles.dropdownList}>
              {availableAgencies.map(agency => (
                <button
                  key={agency.account_id}
                  className={styles.dropdownItem}
                  onClick={() => handleSelectAgency(agency)}
                  type="button"
                >
                  {agency.display_name}
                </button>
              ))}
            </div>
          )}
        <textarea
          className={styles.messageTextarea}
          rows={2}
          placeholder="Enter starting message..."
          value={message}
          onChange={handleMessageChange}
          disabled={createStates.isLoading}
        />
      </ModalBody>
      <ModalFooter py="16px" px="24px" stacked flow={12}>
        {isCreateError && (
          <div className={styles.errorMessage}>
            <AttentionTriangleIcon width={14} height={14} color="lightDanger" />
            Failed to create conversation. Please try again.
          </div>
        )}
        <div className={styles.footerButtons}>
          <Button btnType="tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            btnType="primary"
            onClick={handleStartConversation}
            disabled={selectedAgencies.length === 0 || !message.trim()}
            isLoadingProp={createStates.isLoading}
          >
            Send
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default NewAgencyConversationModal;
